import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RenderImage from '../../pages/Agent/ChatPage/components/TabComponent/RenderImage';
import downloadIcon from '@/pages/aigc/assets/svg/download.svg';
import closeIcon from '@/pages/aigc/assets/svg/close.svg';
import './index.scss';
import { createPortal } from 'react-dom';

interface IImagePreviewProps {
  defaultVisible?: boolean;
  src: string;
  name?: string;
  onClose?: () => void;
  onVisibleChange?: (visible: boolean) => void;
  downloadName?: string;
  className?: string;
  defaultSrc?: string;
  haveLoading?: boolean;
}

const ImagePreview: React.FC<IImagePreviewProps> = ({
  defaultVisible = false,
  src,
  name = '',
  onClose,
  onVisibleChange,
  downloadName,
  className,
  defaultSrc = '',
  haveLoading,
}) => {
  const [internalVisible, setInternalVisible] = useState<boolean>(defaultVisible);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const effectiveVisible = useMemo(() => internalVisible, [internalVisible]);

  const handleOpen = useCallback(() => {
    // if (isLoading || hasError) return;
    if (hasError) return;
    setInternalVisible(true);
    onVisibleChange?.(true);
  }, [hasError, onVisibleChange]);

  const handleClosePreview = useCallback(() => {
    setInternalVisible(false);
    onVisibleChange?.(false);
    onClose?.();
  }, [onVisibleChange, onClose]);

  const handleDownload = useCallback(async (e) => {
    e?.stopPropagation?.();
    try {
      const filename = downloadName || name || 'image';
      const isData = typeof src === 'string' && src.startsWith('data:');
      const isBlob = typeof src === 'string' && src.startsWith('blob:');
      const isHttp = typeof src === 'string' && src.startsWith('http:');

      // 1) data:/blob: 直接触发下载（避免再发起请求）
      if (isData || isBlob) {
        const a = document.createElement('a');
        a.href = src;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // 2) http: 强制升级为 https:
      const normalizedUrl = isHttp ? src.replace(/^http:/, 'https:') : src;

      // 3) https/CORS 下载为 blob 再保存
      const res = await fetch(normalizedUrl, {
        mode: 'cors',
        credentials: 'include',
        referrerPolicy: 'no-referrer',
      });
      if (!res.ok) throw new Error('download request failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // 4) 兜底：直接打开链接（也尝试 http->https）
      try {
        const a = document.createElement('a');
        a.href = (typeof src === 'string' ? src.replace(/^http:/, 'https:') : src) as string;
        a.download = downloadName || name || 'image';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch {}
      console.log('handleDownload~下载失败');
    }
  }, [src, name, downloadName]);

  useEffect(() => {
    if (!effectiveVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClosePreview();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [effectiveVisible, handleClosePreview]);

  // 渲染预览层到 body 的 Portal 组件
  const renderPreviewOverlay = () => {
    if (!effectiveVisible) return null;

    return createPortal(
      <div className="dba-img-preview-overlay" onClick={handleClosePreview}>
        <div className="dba-img-preview-header">
          <div className="dba-img-preview-title" title={name}>{name || ''}</div>
          <div className="dba-img-preview-actions">
            <img
              className='dba-img-preview-action-btn'
              src={downloadIcon}
              onClick={(e) => void handleDownload(e)}
            />
            <img
              className='dba-img-preview-action-btn'
              src={closeIcon}
              onClick={handleClosePreview}
            />
          </div>
        </div>
        <div className="dba-img-preview-content">
          <div onClick={(e => e.stopPropagation())}>
            <RenderImage
              src={src}
              className="dba-img-preview-img"
              alt={name}
              defaultSrc={defaultSrc}
              haveLoading={haveLoading} />
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <span onClick={handleOpen} style={{ cursor: isLoading || hasError ? 'default' : 'zoom-in' }}>
        <RenderImage
          src={src}
          className={className}
          alt={name}
          defaultSrc={defaultSrc}
          haveLoading={haveLoading}
          onLoad={() => {
            setIsLoading(false);
            setHasError(false);
          }}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      </span>
      {renderPreviewOverlay()}
    </>
  );
};

export default ImagePreview;